import {
  getResponsesTableQueryKey,
  updateResponsesTableCell,
  updateResponsesTableCellMutation,
  useHeyApi,
} from "@platform/api";
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";

import useToastNotificationService from "../../../../../../hooks/useToastNotificationService";

import type {
  ResponseCell,
  ResponsesTableResponse,
  UpdateResponseCellRequest,
} from "@platform/api";
import type { AxiosError } from "axios";

type MutateContext = { previous: ResponsesTableResponse | undefined };
type MutateVariables = Parameters<typeof updateResponsesTableCell>[0];

interface UseUpdateResponseCellOptions {
  rfpId: string;
}

/**
 * Mutation hook for persisting an analyst's manual status selection for a
 * carrier × coverage cell. Uses optimistic updates so the grid reflects the
 * new value immediately; rolls back with a toast on failure.
 */
export const useUpdateResponseCell = ({
  rfpId,
}: UseUpdateResponseCellOptions): UseMutationResult<
  ResponseCell,
  AxiosError<Error>,
  MutateVariables,
  MutateContext
> => {
  const { centroPortalClient } = useHeyApi();
  const queryClient = useQueryClient();
  const { createToastNotification } = useToastNotificationService();

  const queryKey = getResponsesTableQueryKey({
    path: { id: rfpId },
    client: centroPortalClient,
  });

  return useMutation<
    ResponseCell,
    AxiosError<Error>,
    MutateVariables,
    MutateContext
  >({
    ...updateResponsesTableCellMutation({ client: centroPortalClient }),
    onMutate: async (variables) => {
      const body = variables.body as UpdateResponseCellRequest;

      // Cancel any in-flight refetches to avoid overwriting the optimistic update.
      await queryClient.cancelQueries({ queryKey });

      const previous =
        queryClient.getQueryData<ResponsesTableResponse>(queryKey);

      queryClient.setQueryData<ResponsesTableResponse>(queryKey, (old) => {
        if (!old) return old;

        const { carrierId, planGroupId, manualStatus } = body;
        const existingCellIndex = old.cells.findIndex(
          (c) => c.carrierId === carrierId && c.planGroupId === planGroupId,
        );

        const updatedCells = [...old.cells];
        if (existingCellIndex >= 0) {
          updatedCells[existingCellIndex] = {
            ...updatedCells[existingCellIndex],
            manualStatus,
          };
        } else {
          updatedCells.push({
            carrierId,
            planGroupId,
            proposalStatus: "pending",
            manualStatus,
          });
        }

        return { ...old, cells: updatedCells };
      });

      return { previous };
    },
    onError: (_err, _variables, context) => {
      // Roll back to the pre-mutation data.
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      createToastNotification({
        severity: "error",
        message: "Failed to save status override. Please try again.",
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });
};
